import { API } from '../config';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Shield, Lock, Download, Trash2, HardDrive, Key } from 'lucide-react';
import { toast } from 'sonner';


export function SettingsDialog({ open, onClose }) {
  const { user, getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('security');
  const [appLockEnabled, setAppLockEnabled] = useState(user?.app_lock_enabled || false);
  const [lockPin, setLockPin] = useState('');
  const [lockTimeout, setLockTimeout] = useState(300);
  const [disablePin, setDisablePin] = useState('');
  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  // PIN
  const [newPin, setNewPin] = useState('');
  const [removePin, setRemovePin] = useState('');
  const [pinIsSet, setPinIsSet] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/security/pin/status`, { headers: getAuthHeaders() })
        .then(res => setPinIsSet(res.data.pin_is_set))
        .catch(() => {});
    }
  }, [open, getAuthHeaders]);
  const [backupPassphrase, setBackupPassphrase] = useState('');
  const [backupPlatform, setBackupPlatform] = useState('');
  const [backups, setBackups] = useState([]);
  const [backupsLoaded, setBackupsLoaded] = useState(false);

  const handleEnableAppLock = async () => {
    if (lockPin.length < 4) { toast.error('PIN en az 4 haneli olmalıdır'); return; }
    try { await axios.post(`${API}/security/app-lock/enable`, { pin: lockPin, timeout_seconds: lockTimeout }, { headers: getAuthHeaders() }); setAppLockEnabled(true); setLockPin(''); toast.success('Uygulama kilidi etkinleştirildi'); } catch { toast.error('Etkinleştirilemedi'); }
  };
  const handleDisableAppLock = async () => {
    try { await axios.post(`${API}/security/app-lock/disable`, { pin: disablePin }, { headers: getAuthHeaders() }); setAppLockEnabled(false); setDisablePin(''); toast.success('Uygulama kilidi devre dışı bırakıldı'); } catch (err) { toast.error(err.response?.data?.detail || 'Başarısız'); }
  };
  const loadBackups = async () => { try { const res = await axios.get(`${API}/backup/list`, { headers: getAuthHeaders() }); setBackups(res.data); setBackupsLoaded(true); } catch { toast.error('Yedekler yüklenemedi'); } };
  const handleCreateBackup = async () => {
    if (!backupPassphrase) { toast.error('Parola gerekli'); return; }
    try { const res = await axios.post(`${API}/backup/create`, { passphrase: backupPassphrase, platform: backupPlatform || undefined }, { headers: getAuthHeaders() }); toast.success(`Yedek oluşturuldu: ${res.data.message_count} mesaj`); setBackupPassphrase(''); loadBackups(); } catch { toast.error('Yedek oluşturulamadı'); }
  };
  const handleDownloadBackup = async (id) => { try { const res = await axios.get(`${API}/backup/${id}/download`, { headers: getAuthHeaders(), responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `yedek_${id}.enc`; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('İndirme başarısız'); } };
  const handleDeleteBackup = async (id) => { try { await axios.delete(`${API}/backup/${id}`, { headers: getAuthHeaders() }); toast.success('Yedek silindi'); loadBackups(); } catch { toast.error('Silinemedi'); } };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-xl max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Ayarlar</DialogTitle></DialogHeader>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="security"><Lock className="w-3 h-3 mr-1" /> Güvenlik</TabsTrigger>
            <TabsTrigger value="password"><Key className="w-3 h-3 mr-1" /> Şifre</TabsTrigger>
            <TabsTrigger value="backup" onClick={() => { if (!backupsLoaded) loadBackups(); }}><HardDrive className="w-3 h-3 mr-1" /> Yedekler</TabsTrigger>
          </TabsList>
          <TabsContent value="security"><ScrollArea className="max-h-[50vh]"><div className="space-y-4 p-1">
            <div className="bg-secondary/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3"><div><h3 className="text-sm font-medium">Uygulama Kilidi</h3><p className="text-xs text-muted-foreground">Uygulama açılırken PIN gerektirir</p></div>
                <Badge variant={appLockEnabled ? 'default' : 'secondary'} className="text-xs">{appLockEnabled ? 'Aktif' : 'Devre Dışı'}</Badge></div>
              {!appLockEnabled ? (<div className="space-y-2">
                <Input placeholder="4 haneli PIN belirleyin" type="password" maxLength={4} value={lockPin} onChange={e => setLockPin(e.target.value.replace(/\D/g, ''))} className="rounded-xl" />
                <select value={lockTimeout} onChange={e => setLockTimeout(Number(e.target.value))} className="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm">
                  <option value={60}>1 dakika sonra kilitle</option><option value={300}>5 dakika sonra kilitle</option><option value={900}>15 dakika sonra kilitle</option><option value={1800}>30 dakika sonra kilitle</option>
                </select>
                <Button onClick={handleEnableAppLock} className="rounded-xl w-full" size="sm">Uygulama Kilidini Etkinleştir</Button>
              </div>) : (<div className="space-y-2">
                <Input placeholder="Devre dışı bırakmak için mevcut PIN'i girin" type="password" maxLength={4} value={disablePin} onChange={e => setDisablePin(e.target.value.replace(/\D/g, ''))} className="rounded-xl" />
                <Button onClick={handleDisableAppLock} variant="destructive" className="rounded-xl w-full" size="sm">Uygulama Kilidini Devre Dışı Bırak</Button>
              </div>)}
            </div>
            {/* 6-Digit PIN */}
            <div className="bg-secondary/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="text-sm font-medium">6 Haneli PIN</h3><p className="text-xs text-muted-foreground">Uygulama girişinde ek güvenlik</p></div>
                <Badge variant={pinIsSet ? 'default' : 'secondary'} className="text-xs">{pinIsSet ? 'Aktif' : 'Devre Dışı'}</Badge>
              </div>
              {!pinIsSet ? (
                <div className="space-y-2">
                  <Input placeholder="6 haneli PIN belirleyin" type="password" maxLength={6} value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} className="rounded-xl text-center tracking-widest" />
                  <Button className="rounded-xl w-full" size="sm" disabled={newPin.length !== 6} onClick={async () => {
                    try { await axios.post(`${API}/security/pin/set`, { pin: newPin }, { headers: getAuthHeaders() }); toast.success('PIN belirlendi'); setPinIsSet(true); setNewPin(''); } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
                  }}>PIN Belirle</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input placeholder="Mevcut PIN'inizi girin" type="password" maxLength={6} value={removePin} onChange={e => setRemovePin(e.target.value.replace(/\D/g, ''))} className="rounded-xl text-center tracking-widest" />
                  <Button variant="destructive" className="rounded-xl w-full" size="sm" disabled={removePin.length !== 6} onClick={async () => {
                    try { await axios.post(`${API}/security/pin/remove`, { pin: removePin }, { headers: getAuthHeaders() }); toast.success('PIN kaldırıldı'); setPinIsSet(false); setRemovePin(''); sessionStorage.removeItem('pin_verified'); } catch (err) { toast.error(err.response?.data?.detail || 'Geçersiz PIN'); }
                  }}>PIN Kaldır</Button>
                </div>
              )}
            </div>
          </div></ScrollArea></TabsContent>
          <TabsContent value="password"><ScrollArea className="max-h-[50vh]"><div className="space-y-4 p-1">
            <div className="bg-secondary/30 rounded-xl p-4">
              <h3 className="text-sm font-medium mb-2">Şifre Değiştir</h3>
              <p className="text-xs text-muted-foreground mb-3">Hesap şifrenizi güvenli bir şekilde değiştirin.</p>
              <div className="space-y-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Mevcut Şifre</label>
                  <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Mevcut şifrenizi girin" className="rounded-xl" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Yeni Şifre</label>
                  <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Yeni şifre" className="rounded-xl" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">Yeni Şifre Tekrar</label>
                  <Input type="password" value={newPwConfirm} onChange={e => setNewPwConfirm(e.target.value)} placeholder="Yeni şifreyi tekrar girin" className="rounded-xl" />
                </div>
                <Button className="rounded-xl w-full" size="sm" onClick={async () => {
                  if (newPw !== newPwConfirm) { toast.error('Yeni şifreler eşleşmiyor.'); return; }
                  if (!currentPw || !newPw) { toast.error('Tüm alanları doldurun.'); return; }
                  try {
                    await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw, new_password_confirm: newPwConfirm }, { headers: getAuthHeaders() });
                    toast.success('Şifre başarıyla değiştirildi.');
                    setCurrentPw(''); setNewPw(''); setNewPwConfirm('');
                  } catch (err) { toast.error(err.response?.data?.detail || 'Şifre değiştirilemedi.'); }
                }}>Şifre Değiştir</Button>
              </div>
            </div>
          </div></ScrollArea></TabsContent>
          <TabsContent value="backup"><ScrollArea className="max-h-[50vh]"><div className="space-y-4 p-1">
            <div className="bg-secondary/30 rounded-xl p-4">
              <h3 className="text-sm font-medium mb-2">Şifreli Yedek Oluştur</h3>
              <p className="text-xs text-muted-foreground mb-3">Mesajlar parolanızla şifrelenir. Düz metin saklanmaz.</p>
              <div className="space-y-2">
                <Input placeholder="Şifreleme parolası" type="password" value={backupPassphrase} onChange={e => setBackupPassphrase(e.target.value)} className="rounded-xl" />
                <select value={backupPlatform} onChange={e => setBackupPlatform(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm">
                  <option value="">Tüm Platformlar</option><option value="telegram">Yalnızca Telegram</option><option value="whatsapp">Yalnızca WhatsApp</option>
                </select>
                <Button onClick={handleCreateBackup} className="rounded-xl w-full" size="sm"><Key className="w-3 h-3 mr-1" /> Yedek Oluştur</Button>
              </div>
            </div>
            {backups.length > 0 && (<div className="space-y-2"><h3 className="text-sm font-medium">Önceki Yedekler</h3>
              {backups.map(b => (<div key={b.id} className="bg-card rounded-xl border border-border/70 p-3 flex items-center justify-between">
                <div><p className="text-xs font-medium">{new Date(b.created_at).toLocaleString('tr-TR')}</p><p className="text-[10px] text-muted-foreground">{b.message_count} mesaj | {b.platform} | {Math.round(b.size / 1024)}KB</p></div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={() => handleDownloadBackup(b.id)}><Download className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-destructive" onClick={() => handleDeleteBackup(b.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>))}
            </div>)}
          </div></ScrollArea></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
